/**
 *
 * @module subscription
 *
 */

import Check from 'check-types';

import Services from './../services/entry';
import Product from './product';
import SubscriptionSource from './subscriptionSource';
import SubscriptionProvider from './subscriptionProvider';

const Account = Services.Account.Account;

const ProductRule = Services.Subscription.ProductRule;
const SubscriptionExpiryType = Services.Subscription.SubscriptionExpiryType;
const SubscriptionStatus = Services.Subscription.SubscriptionStatus;
const SubscriptionType = Services.Subscription.SubscriptionType;

const Util = Services.Util;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    array,
    boolean,
    date,
    instanceStrict,
    nonEmptyObject,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class Subscription
 * @desc Represents the subscription data the services provide.
 *
 */
export default class Subscription {

    /**
     *
     * @param {Object} options
     * @param {String} [options.accountId]
     * @param {String} [options.deviceId]
     * @param {String} [options.expirationDate]
     * @param {String} [options.expiryType=SubscriptionExpiryType.UNKNOWN]
     * @param {String} [options.externalIdentity]
     * @param {String} options.id
     * @param {Boolean} options.isActive
     * @param {String} [options.lastSyncDate]
     * @param {String} [options.nextRenewalDate]
     * @param {Array<Object>} options.products
     * @param {Object} [options.rule]
     * @param {Object} options.source
     * @param {String} [options.startDate]
     * @param {Object} options.status
     * @param {String} [options.type]
     * @param {String} [options.purchaseDate]
     *
     */
    constructor(options) {

        const errorMessage = {
            ctor: 'Subscription(options)',
            get options() {
                return `${this.ctor} options should be an Object`;
            },
            get accountId() {
                return `${this.ctor} accountId should be a String with length`;
            },
            get deviceId() {
                return `${this.ctor} deviceId should be a String with length`;
            },
            get expirationDate() {
                return `${this.ctor} expirationDate should be a String with length`;
            },
            get expiryType() {
                return `${this.ctor} expiryType should be a String with length`;
            },
            get externalIdentity() {
                return `${this.ctor} externalIdentity should be a String with length`;
            },
            get id() {
                return `${this.ctor} id should be a String with length`;
            },
            get isActive() {
                return `${this.ctor} isActive should be a Boolean`;
            },
            get lastSyncDate() {
                return `${this.ctor} lastSyncDate should be a String with length`;
            },
            get nextRenewalDate() {
                return `${this.ctor} nextRenewalDate should be a String with length`;
            },
            get products() {
                return `${this.ctor} products needs Array of Objects`;
            },
            get rule() {
                return `${this.ctor} rule should be an Object`;
            },
            get source() {
                return `${this.ctor} source should be an Object`;
            },
            get startDate() {
                return `${this.ctor} startDate should be a String with length`;
            },
            get status() {
                return `${this.ctor} status should be an Object`;
            },
            get type() {
                return `${this.ctor} type should be a String with length`;
            },
            get purchaseDate() {
                return `${this.ctor} options.purchaseDate should be a String with length and conform to ISO 8601`;
            }
        };


        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { accountId, deviceId, expirationDate, expiryType, externalIdentity } = options;
        const { id, isActive, lastSyncDate, nextRenewalDate, products = [] } = options;
        const { rule, source, startDate, status, type, purchaseDate } = options;

        if (Check.assigned(accountId)) {
            nonEmptyString(accountId, errorMessage.accountId, InvalidArgumentException);
        }

        if (Check.assigned(deviceId)) {
            nonEmptyString(deviceId, errorMessage.deviceId, InvalidArgumentException);
        }

        if (Check.assigned(expirationDate)) {
            nonEmptyString(expirationDate, errorMessage.expirationDate, InvalidArgumentException);
        }

        if (Check.assigned(expiryType)) {
            nonEmptyString(expiryType, errorMessage.expiryType, InvalidArgumentException);
        }

        if (Check.assigned(externalIdentity)) {
            nonEmptyString(externalIdentity, errorMessage.externalIdentity, InvalidArgumentException);
        }

        nonEmptyString(id, errorMessage.id, InvalidArgumentException);
        boolean(isActive, errorMessage.isActive, InvalidArgumentException);

        if (Check.assigned(lastSyncDate)) {
            nonEmptyString(lastSyncDate, errorMessage.lastSyncDate, InvalidArgumentException);
        }

        if (Check.assigned(nextRenewalDate)) {
            nonEmptyString(nextRenewalDate, errorMessage.nextRenewalDate, InvalidArgumentException);
        }

        array.of.instanceStrict(products, Object, errorMessage.products, InvalidArgumentException);

        const productsArray = [];

        if (Check.nonEmptyArray(products)) {
            products.forEach((product) => {
                productsArray.push(new Product(product));
            });
        }

        if (Check.assigned(rule)) {
            nonEmptyObject(rule, errorMessage.rule, InvalidArgumentException);
        }

        instanceStrict(source, Object, errorMessage.source, InvalidArgumentException);

        if (Check.assigned(startDate)) {
            nonEmptyString(startDate, errorMessage.startDate, InvalidArgumentException);
        }

        instanceStrict(status, Object, errorMessage.status, InvalidArgumentException);

        if (Check.assigned(type)) {
            nonEmptyString(type, errorMessage.type, InvalidArgumentException);
        }

        if (Check.assigned(purchaseDate) && !Util.dateRegex(purchaseDate)) {
            date(null, errorMessage.purchaseDate, InvalidArgumentException);
        }

        /**
         *
         * @access public
         * @type {Date|null}
         * @desc Gets the expiration date of the subscription.
         *
         */
        this.expirationDate = expirationDate ? new Date(expirationDate) : null;

        /**
         *
         * @access public
         * @type {SDK.Services.Subscription.SubscriptionExpiryType}
         * @desc Gets the expiry type.
         *
         */
        this.expiryType = SubscriptionExpiryType[expiryType] || SubscriptionExpiryType.UNKNOWN;

        /**
         *
         * @access public
         * @type {String}
         * @desc Gets the URN associated with the subscription.
         *
         */
        this.id = id;

        /**
         *
         * @access public
         * @type {Boolean}
         * @gets Gets the boolean value of whether is active or not.
         *
         */
        this.isActive = isActive;

        /**
         *
         * @access public
         * @type {Date|null}
         * @desc Gets the date of the subscription's next renewal.
         *
         */
        this.nextRenewalDate = nextRenewalDate ? new Date(nextRenewalDate) : null;

        /**
         *
         * @access public
         * @type {Array<SDK.Subscription.Product>}
         * @desc Gets the array of Products.
         *
         */
        this.products = productsArray;

        /**
         *
         * @access public
         * @type {Date|null}
         * @desc Gets the start date of the subscription.
         *
         */
        this.startDate = startDate ? new Date(startDate) : null;

        /**
         *
         * @access public
         * @type {SDK.Services.Subscription.SubscriptionStatus}
         * @desc Gets the status of the subscription.
         *
         */
        this.status = new SubscriptionStatus(status);

        /**
         *
         * @access public
         * @type {SDK.Subscription.SubscriptionProvider}
         *
         */
        this.provider = SubscriptionProvider[source.provider] || SubscriptionProvider.OTHER;

        /**
         *
         * @access private
         * @type {String|undefined}
         * @desc Gets the ID of the account the subscription is associated with.
         *
         */
        this.accountId = accountId;

        /**
         *
         * @access private
         * @type {String|undefined}
         * @desc Gets the ID of the device the subscription is associated with.
         *
         */
        this.deviceId = deviceId;

        /**
         *
         * @access private
         * @type {String|undefined}
         * @desc Gets the external identity.
         *
         */
        this.externalIdentity = externalIdentity;

        /**
         *
         * @access private
         * @type {Date|null}
         * @desc Gets the date of the last time the subscription was synchronized.
         *
         */
        this.lastSyncDate = lastSyncDate ? new Date(lastSyncDate) : null;

        /**
         *
         * @access public
         * @type {SDK.Services.Subscription.ProductRule|null}
         * @desc Gets the product rule.
         *
         */
        this.rule = rule ? new ProductRule(rule) : null;

        /**
         *
         * @access private
         * @type {SDK.Services.Subscription.SubscriptionSource}
         * @desc Gets the source of the subscription.
         *
         */
        this.source = new SubscriptionSource(source);

        /**
         *
         * @access private
         * @type {SDK.Services.Subscription.SubscriptionType|undefined}
         * @desc Gets the type of the subscription.
         *
         */
        this.type = SubscriptionType[type];

        /**
         *
         * @access private
         * @since 3.9.0
         * @type {String|undefined}
         * @note ("YYYY-MM-DDThh:mm:ss.sssZ") Should conform to ISO-8601.
         * @desc The date which the subscription is purchased.
         *
         */
        this.purchaseDate = purchaseDate;

    }

    /**
     *
     * @access public
     * @desc Checks a subscription to determine if it is already bound to a device.
     * @returns {Boolean}
     *
     */
    isBoundToDevice() {

        return Check.nonEmptyString(this.deviceId);

    }

    /**
     *
     * @access public
     * @desc Checks a subscription to determine if it is already bound to an account.
     * @param {SDK.Services.Account.Account} [account=null]
     * @returns {Boolean}
     *
     */
    isBoundToAccount(account = null) {

        const { accountId } = this;

        const hasAccountId = Check.assigned(accountId);

        if (hasAccountId && Check.instanceStrict(account, Account)) {
            return Check.equal(accountId, account.accountId);
        }

        return hasAccountId;

    }

}



// WEBPACK FOOTER //
// ./src/subscription/subscription.js